import { Link } from 'react-router-dom';

type ServiceKey =
  | 'blob-storage'
  | 'logic-apps'
  | 'functions'
  | 'app-service'
  | 'bandwidth'
  | 'log-analytics'
  | 'networking'
  | 'data-factory'
  | 'virtual-desktop'
  | 'front-door'
  | 'firewall'
  | 'sql-database'
  | 'azure-openai'
  | 'managed-disks'
  | 'key-vault'
  | 'azure-devops'
  | 'site-recovery';

const SERVICE_META: Record<ServiceKey, { label: string; description: string; icon: string }> = {
  'blob-storage': {
    label: 'Blob Storage Calculator',
    description: 'Estimate Hot, Cool, Cold and Archive tier costs for UK South.',
    icon: 'storage',
  },
  'logic-apps': {
    label: 'Logic Apps Calculator',
    description: 'Calculate action execution costs for Consumption and Standard plans.',
    icon: 'account_tree',
  },
  functions: {
    label: 'Azure Functions Calculator',
    description: 'Estimate GB-second and execution costs for Consumption and Premium plans.',
    icon: 'function',
  },
  'app-service': {
    label: 'App Service Calculator',
    description: 'Compare Windows and Linux tiers from Free to Premium v3.',
    icon: 'web',
  },
  bandwidth: {
    label: 'Bandwidth & Egress Calculator',
    description: 'Tiered outbound internet and inter-region egress costs.',
    icon: 'upload',
  },
  'log-analytics': {
    label: 'Log Analytics & Sentinel Calculator',
    description: 'Ingestion, retention, search job and restore costs for UK South.',
    icon: 'monitor_heart',
  },
  networking: {
    label: 'Azure Networking Cost Calculator',
    description: 'NAT Gateway, VNet peering, Public IP, and DDoS Protection costs for UK South.',
    icon: 'hub',
  },
  'data-factory': {
    label: 'Azure Data Factory Cost Calculator',
    description: 'DIU-hours, Data Flow vCore costs, Self-hosted IR charges and pipeline orchestration.',
    icon: 'account_tree',
  },
  'virtual-desktop': {
    label: 'Azure Virtual Desktop Calculator',
    description: 'Estimate session host, FSLogix storage, and user licensing costs for UK South.',
    icon: 'desktop_windows',
  },
  'front-door': {
    label: 'Azure Front Door Calculator',
    description: 'Estimate Front Door Standard, Premium, and Classic tier costs for UK South.',
    icon: 'language',
  },
  firewall: {
    label: 'Azure Firewall Calculator',
    description: 'Compare Basic, Standard, and Premium with data processing and logging cost estimates.',
    icon: 'shield',
  },
  'sql-database': {
    label: 'Azure SQL Database Calculator',
    description: 'Compare DTU vs vCore costs with AHB, Reserved Instances, and Savings Plan for UK South.',
    icon: 'database',
  },
  'azure-openai': {
    label: 'Azure OpenAI Calculator',
    description: 'Estimate GPT-4o Standard PAYG vs PTU costs with break-even analysis in GBP.',
    icon: 'smart_toy',
  },
  'managed-disks': {
    label: 'Managed Disks Calculator',
    description: 'Compare Standard HDD vs SSD transaction costs and Premium SSD pricing for UK South.',
    icon: 'storage',
  },
  'key-vault': {
    label: 'Key Vault Cost Calculator',
    description: 'Estimate Key Vault Standard, Premium, and Managed HSM costs for UK South.',
    icon: 'lock',
  },
  'azure-devops': {
    label: 'Azure DevOps Cost Calculator',
    description: 'Estimate parallel jobs, user licensing, and artifact storage costs for Azure DevOps.',
    icon: 'code',
  },
  'site-recovery': {
    label: 'Site Recovery Cost Calculator',
    description: 'Estimate the full Azure Site Recovery DR cost: instance fees, replica storage, replication transfer, and failover compute.',
    icon: 'cloud_sync',
  },
};

interface CalculatorEmbedProps {
  service: ServiceKey;
}

export function CalculatorEmbed({ service }: CalculatorEmbedProps) {
  const meta = SERVICE_META[service];

  return (
    <Link
      to={`/${service}`}
      className="group flex items-center gap-4 px-5 py-4 rounded transition-colors"
      style={{ backgroundColor: 'var(--surface-container)', border: '1px solid rgba(0,120,212,0.4)' }}
    >
      <span className="material-symbols-outlined text-[#0078d4] text-[28px] shrink-0">
        {meta.icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold transition-colors" style={{ color: 'var(--text-secondary)' }}>
          {meta.label}
        </p>
        <p className="text-[12px] mt-0.5 leading-snug" style={{ color: 'var(--text-tertiary)' }}>{meta.description}</p>
      </div>
      <span className="material-symbols-outlined text-[#0078d4] text-[20px] shrink-0 group-hover:translate-x-0.5 transition-transform">
        arrow_forward
      </span>
    </Link>
  );
}
